import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { Business } from '@marketing-milk/interfaces'

interface BusinessSelectionProps {
  businesses: Pick<Business, 'id' | 'businessName'>[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export function BusinessSelection({ businesses, selectedIds, onChange }: BusinessSelectionProps) {
  const [search, setSearch] = useState('')

  const filteredBusinesses = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) {
      return businesses
    }
    return businesses.filter(b => b.businessName.toLowerCase().includes(term))
  }, [businesses, search])

  const toggleBusiness = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(i => i !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const allFilteredSelected =
    filteredBusinesses.length > 0 && filteredBusinesses.every(b => selectedIds.includes(b.id))

  const handleSelectAll = () => {
    const filteredIds = filteredBusinesses.map(b => b.id)
    if (allFilteredSelected) {
      onChange(selectedIds.filter(id => !filteredIds.includes(id)))
    } else {
      onChange(Array.from(new Set([...selectedIds, ...filteredIds])))
    }
  }

  return (
    <Box>
      <Box style={{ display: 'flex', alignItems: 'center' }} className="pb-2">
        <TextField
          placeholder="Search businesses"
          fullWidth
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          onClick={handleSelectAll}
          color="primary"
          style={{ whiteSpace: 'nowrap', marginLeft: 8 }}
        >
          {allFilteredSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </Box>
      <Typography variant="caption" color="textSecondary">
        {selectedIds.length} selected
      </Typography>
      <List
        dense
        style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid lightgrey',
          borderRadius: 4,
          marginTop: 4,
        }}
      >
        {filteredBusinesses.length === 0 && (
          <ListItem>
            <ListItemText primary="No businesses found" />
          </ListItem>
        )}
        {filteredBusinesses.map(b => (
          <ListItem key={b.id} button onClick={() => toggleBusiness(b.id)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedIds.includes(b.id)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={b.businessName} />
          </ListItem>
        ))}
      </List>
    </Box>
  )
}
